import { XIcon } from "lucide-react";
import { Dialog as SheetPrimitive } from "radix-ui";
import type * as React from "react";

import { cn } from "@/lib/utils";

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "fixed inset-0 z-60 bg-surface-950/60 backdrop-blur-sm",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  side = "right",
  showCloseButton = true,
  scrollable = true,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left";
  showCloseButton?: boolean;
  scrollable?: boolean;
}) {
  return (
    <SheetPrimitive.Portal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          "fixed z-60 flex flex-col bg-surface-900 shadow-[0_0_60px_-15px_rgba(38,217,181,0.15)] outline-none",
          "data-[state=open]:animate-in data-[state=closed]:animate-out transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
          side === "right" &&
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-full max-w-[400px] border-l border-surface-700/50",
          side === "left" &&
            "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-full max-w-[400px] border-r border-surface-700/50",
          side === "top" &&
            "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b border-surface-700/50",
          side === "bottom" &&
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t border-surface-700/50",
          className,
        )}
        {...props}
      >
        {/* Left accent glow (for right-side sheet) */}
        {side === "right" && (
          <div className="absolute inset-y-0 left-0 w-px bg-gradient-to-b from-transparent via-brand-500/40 to-transparent" />
        )}

        {/* Atmospheric radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_rgba(38,217,181,0.06)_0%,_transparent_60%)] pointer-events-none" />

        {/* Noise texture */}
        <div className="absolute inset-0 noise-overlay pointer-events-none" />

        {/* Close button */}
        {showCloseButton && (
          <SheetPrimitive.Close className="absolute top-4 right-4 z-10 p-1.5 rounded-lg text-surface-500 hover:text-white hover:bg-surface-700/60 transition-colors cursor-pointer">
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}

        {/* Content */}
        <div className={cn("relative h-full", scrollable ? "overflow-y-auto" : "overflow-hidden")}>
          {children}
        </div>
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  );
}

function SheetHeader({ children, subtitle }: { children: React.ReactNode; subtitle?: string }) {
  return (
    <div className="px-6 pt-6 pb-1">
      <SheetPrimitive.Title className="text-xl font-bold text-white tracking-wide">
        {children}
      </SheetPrimitive.Title>
      {subtitle && (
        <SheetPrimitive.Description className="text-sm text-surface-400 mt-1">
          {subtitle}
        </SheetPrimitive.Description>
      )}
    </div>
  );
}

function SheetBody({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn("px-6 py-4", className)}>{children}</div>;
}

export { Sheet, SheetTrigger, SheetClose, SheetContent, SheetHeader, SheetBody };
